import React from 'react'
import PropTypes from 'prop-types'
import Loader from 'components/Loader'
import Chart from './Chart'
import classnames from 'classnames'
import Tooltip from 'components/Tooltip'
import partial from 'utils/partial'

const radian = deg => deg * (Math.PI / 180)

export default class RadialChart extends React.Component {
  static propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    data: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number,
      color: PropTypes.string
    })),
    innerRadius: PropTypes.number,
    noToolTip: PropTypes.bool,
    className: PropTypes.string
  }

  constructor(props) {
    super(props)
    this.state = {
      selectedIndex: null
    }
  }

  render() {
    const {className} = this.props

    return (
      <div className={classnames('radial-chart', className)}>
        {this.renderBody()}
      </div>
    )
  }

  renderBody() {
    const {data, height, children} = this.props
    if (!data) return <Loader />

    const pieces = this.getPieces()

    return (
      <div className='radial-chart_body'>
        <Chart height={height}>
          {pieces && pieces.map(this.renderPiece)}
        </Chart>
        {this.renderTooltip()}
        {children}
      </div>
    )
  }

  renderPiece = ({start, end, color}, index) => {
    const {selectedIndex} = this.state
    const radius = this.getRadius()
    const innerRadius = this.getInnerRadius()
    const isFullCircle = (end - start) === 360
    const className = classnames('radial-chart_piece', {
      'is-hover': selectedIndex === index,
      [`is-${color}`]: color,
      'is-full': isFullCircle
    })

    if (isFullCircle) {
      return (<circle
        cx={this.getCenter()[0]}
        cy={this.getCenter()[1]}
        r={0.5 * (radius + innerRadius)}
        strokeWidth={radius - innerRadius}
        onMouseOver={partial(this.onMouseOver, index)}
        onMouseOut={this.onMouseOut}
        className={className}
        key={start + '_' + end}
      />)
    } else {
      return (<path
        d={`
          M ${this.pathCoords(start, radius)}
          A ${radius}, ${radius}, 0, ${end - start > 180 ? 1 : 0}, 0, ${this.pathCoords(end, radius)}
          L ${this.pathCoords(end, innerRadius)}
          A ${innerRadius}, ${innerRadius}, 0, ${end - start > 180 ? 1 : 0}, 1, ${this.pathCoords(start, innerRadius)}
          L ${this.pathCoords(start, radius)} Z
        `}
        onMouseOver={partial(this.onMouseOver, index)}
        onMouseOut={this.onMouseOut}
        className={className}
        key={start + '_' + end}
      />)
    }
  }

  renderTooltip() {
    const {selectedIndex} = this.state
    const {noToolTip} = this.props

    if (!noToolTip && selectedIndex !== null) {
      const {width, height, data} = this.props
      const pieces = this.getPieces(selectedIndex)
      const [xOffset, yOffset] = this.getCenter()
      const {start, end} = pieces[selectedIndex]
      const angle = start - ((start - end) / 2)
      const radius = this.getInnerRadius() + (this.getRadius() - this.getInnerRadius()) / 2
      const [x, y] = this.radialPoint(angle, radius)
      const dataItem = data[selectedIndex]

      return (
        <Tooltip
          x={x + xOffset}
          y={y + yOffset}
          bounds={{x: 0, y: 0, width, height}}
        >
          {dataItem.name} {dataItem.value}
        </Tooltip>
      )
    } else {
      return null
    }
  }

  onMouseOver = index => {
    this.setState({
      selectedIndex: index
    })
  }

  onMouseOut = () => {
    this.setState({
      selectedIndex: null
    })
  }

  getRadius() {
    const {width, height} = this.props
    return Math.min(width, height) / 2
  }

  getInnerRadius() {
    const {innerRadius} = this.props
    if (innerRadius) return innerRadius

    const radius = this.getRadius()
    return radius - radius / 4
  }

  getPieces() {
    const {data} = this.props
    const values = data.map(item => item.value)
    const sum = values.reduce((sum, value) => sum + value, 0)

    if (!sum) return null

    let accAngle = 0
    const pieces = values.map((value, index, arr) => {
      const angle = (value / sum) * 360
      const obj = {
        start: accAngle,
        end: accAngle + angle,
        color: data[index].color
      }
      accAngle += angle
      return obj
    })

    return pieces
  }

  getCenter() {
    const {width, height} = this.props
    return [width / 2, height / 2]
  }

  pathCoords(deg, radius) {
    const offset = this.getCenter()

    return this.radialPoint(deg, radius).map((coord, index) => coord + offset[index]).join(', ')
  }

  radialPoint(deg, radius) {
    return [Math.sin(radian(deg)) * radius, Math.cos(radian(deg)) * radius]
  }
}



// WEBPACK FOOTER //
// ./client/components/Chart/RadialChart.js